import { useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFormDataStore } from "../../../store/formDataStore";
import { WebSocketMessage } from "../types";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { getAccessToken } from "../../../api/clients/tokenAccessor";

const HEARTBEAT_INTERVAL_MS = 30_000; // 30s keepalive to refresh server lock TTL

interface UseReportWebSocketProps {
  liveWebSocket: React.MutableRefObject<WebSocket | null>;
  reportID: string | undefined;
  enableEditing: boolean;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showTimeoutWarning: boolean;
  setWarningState: (state: boolean) => void;
  setInactivityExpiryTimestamp: (timestamp: number) => void;
  refetchIncident: () => void;
  setIsSaving: (saving: boolean) => void;
  saveDeferredRef: React.MutableRefObject<{
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
    timeoutId: NodeJS.Timeout | null;
  } | null>;
  setLastModifiedTime: (date: Date | null) => void;
  setShowRMSCommit: (show: boolean) => void;
  setReportType: (type: string) => void;
  setVideoData: (data: VideoData | VideoData[] | null) => void;
  setWarnings: (warnings: string[]) => void;
}

export const useReportWebSocket = ({
  liveWebSocket,
  reportID,
  enableEditing,
  showError,
  showSuccess,
  showTimeoutWarning,
  setWarningState,
  setInactivityExpiryTimestamp,
  refetchIncident,
  setIsSaving,
  saveDeferredRef,
  setLastModifiedTime,
  setShowRMSCommit,
  setReportType,
  setVideoData,
  setWarnings,
}: UseReportWebSocketProps) => {
  const navigate = useNavigate();

  // Ref to hold latest callback props — avoids stale closures in WS event handlers
  const handlersRef = useRef({
    showError, showSuccess, showTimeoutWarning, setWarningState,
    setInactivityExpiryTimestamp, refetchIncident, setIsSaving,
    setLastModifiedTime, setShowRMSCommit, setReportType,
    setVideoData, setWarnings,
  });
  handlersRef.current = {
    showError, showSuccess, showTimeoutWarning, setWarningState,
    setInactivityExpiryTimestamp, refetchIncident, setIsSaving,
    setLastModifiedTime, setShowRMSCommit, setReportType,
    setVideoData, setWarnings,
  };

  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Connect to live editing socket
    if (liveWebSocket.current === null && enableEditing && reportID) {
      let lastCloseCode: number | null = null;

      const connect = async () => {
        let tokenParam = "";
        try {
          const token = await getAccessToken();
          tokenParam = `&token=${encodeURIComponent(token)}`;
        } catch {
          // Token not available — connect without it; server will reject if required
        }
        const wsURL = `${
          import.meta.env.VITE_WEBSOCKETS_DOMAIN
        }?reportID=${reportID}${tokenParam}`;

        const editingSocket = new WebSocket(wsURL);

      editingSocket.onopen = () => {
        console.log("[WebSocket] Connected");
        // Start client-side heartbeat to keep server lock TTL alive
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
        heartbeatIntervalRef.current = setInterval(() => {
          if (editingSocket.readyState === WebSocket.OPEN) {
            editingSocket.send(JSON.stringify({ action: "heartbeat" }));
          }
        }, HEARTBEAT_INTERVAL_MS);
      };

      editingSocket.onerror = (event) => {
        console.error("[WebSocket] Connection error:", event);
      };

      editingSocket.onclose = (event) => {
        console.log("[WebSocket] Closed:", event.code, event.reason);
        // Stop heartbeat on close
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        const actualCode = lastCloseCode || event.code;
        if (actualCode === 3008) {
          navigate(`/`, {
            replace: true,
            state: { message: "You were redirected due to inactivity." },
          });
        } else if (actualCode === 3009) {
          handlersRef.current.showError(
            "Another user is currently editing this report. Please try again later."
          );
          navigate("/");
        } else if (actualCode === 3002) {
          handlersRef.current.showError("User is not authenticated. Please login again.");
          navigate("/login");
        } else if (actualCode === 3007) {
          handlersRef.current.showError(
            "Lock was lost. Another user may have accessed this report."
          );
          navigate("/");
        } else if (actualCode === 1006) {
          handlersRef.current.showError(
            "Connection to server lost unexpectedly. Please refresh the page to continue editing."
          );
          navigate("/", { replace: true });
        }
      };

      editingSocket.onmessage = async (event) => {
        const data: WebSocketMessage = JSON.parse(event.data);

        if (data.closeCode) {
          lastCloseCode = data.closeCode;
        }

        if (data.closing && data.closeCode) {
          handlersRef.current.showError(data.error || "Connection closing");
          return;
        }

        if (data.type === "sessionHeartbeatWarning") {
          if (!handlersRef.current.showTimeoutWarning && data.expires) {
            handlersRef.current.setInactivityExpiryTimestamp(data.expires);
            handlersRef.current.setWarningState(true);
          }
          return;
        }

        if (data.status === "duplicate") {
          const { destroyStore } = useFormDataStore.getState();
          destroyStore();
          handlersRef.current.showError(
            data.error ||
              "Another user is currently editing this report. Please try again later."
          );
          navigate("/", { replace: true });
          return;
        }

        if (handlersRef.current.showTimeoutWarning) {
          handlersRef.current.setWarningState(false);
        }

        if (data.error) {
          handlersRef.current.showError(data.error);
          handlersRef.current.setIsSaving(false);
          if (saveDeferredRef.current) {
            if (saveDeferredRef.current.timeoutId) {
              clearTimeout(saveDeferredRef.current.timeoutId);
            }
            saveDeferredRef.current.reject(new Error(data.error));
            saveDeferredRef.current = null;
          }
          return;
        }

        if (data.success) {
          handlersRef.current.setIsSaving(false);
          handlersRef.current.showSuccess(data.success);

          if (saveDeferredRef.current) {
            if (saveDeferredRef.current.timeoutId) {
              clearTimeout(saveDeferredRef.current.timeoutId);
            }
            saveDeferredRef.current.resolve();
            saveDeferredRef.current = null;
          }

          if (data.narrativeUpdated) {
            handlersRef.current.refetchIncident();
          }
        }

        if (data.exit) {
          const { destroyStore } = useFormDataStore.getState();
          destroyStore();

          if (data.timeout) {
            navigate(`/`, {
              replace: true,
              state: { message: "You were redirected due to inactivity." },
            });
          } else {
            navigate("/");
          }
          return;
        }

        if (data.newObject) {
          const { setFormData } = useFormDataStore.getState();
          setFormData(data.newObject);
          handlersRef.current.setLastModifiedTime(
            data.lastModified ? new Date(data.lastModified) : new Date()
          );
        }

        if (typeof data.supportsRMS !== "undefined") {
          handlersRef.current.setShowRMSCommit(data.supportsRMS);
        }

        if (data.reportType) {
          handlersRef.current.setReportType(data.reportType);
        }

        if (data.video) {
          handlersRef.current.setVideoData(data.video);
        }

        if (data.warnings && data.warnings.length > 0) {
          handlersRef.current.setWarnings(data.warnings);
        }
      };

        liveWebSocket.current = editingSocket;
      };

      connect();
    }

    if (
      liveWebSocket.current &&
      liveWebSocket.current.readyState === WebSocket.OPEN
    ) {
        // Log or something
    }

    return () => {
      // Cleanup
      console.log("Cleaning up websocket connection");
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      liveWebSocket.current?.close();
      liveWebSocket.current = null;
    };
  }, [reportID, enableEditing]);

  const handleUserAction = useCallback((action: string) => {
    if (showTimeoutWarning || !enableEditing) return;

    if (
      !liveWebSocket.current ||
      liveWebSocket.current.readyState !== WebSocket.OPEN
    ) {
      showError(
        "Connection to server lost. Please refresh the page to make edits."
      );
      return;
    }
    liveWebSocket.current.send(JSON.stringify({ action }));
  }, [showTimeoutWarning, enableEditing, showError]);

  const continueSession = useCallback(() => {
     setWarningState(false);
     if (
      liveWebSocket.current &&
      liveWebSocket.current.readyState === WebSocket.OPEN
    ) {
      liveWebSocket.current.send(
        JSON.stringify({ action: "Continue session" })
      );
    }
  }, [setWarningState]);

  return { handleUserAction, continueSession };
};
